import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import {
  Users,
  Server,
  Phone,
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  DollarSign,
  Activity,
  Shield,
  Zap,
  ArrowUpRight,
  RefreshCw,
  UserPlus,
  ServerCog,
  CreditCard,
  Bell
} from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

interface DashboardStats {
  totalUsers: number;
  totalServers: number;
  totalServices: number;
  totalInvoices: number;
  activeServices: number;
  paidInvoices: number;
  pendingInvoices: number;
  monthlyRevenue: number;
  recentUsers: Array<{
    id: number;
    name: string;
    email: string;
    created_at: string;
  }>;
  recentServices: Array<{
    id: number;
    extension: string;
    status: string;
    User: {
      name: string;
    };
    created_at: string;
  }>;
  serverStatus: Array<{
    id: number;
    name: string;
    host: string;
    status: string;
    services_count: number;
  }>;
}

const AdminDashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [animatedValues, setAnimatedValues] = useState<{[key: string]: number}>({});
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);


  const fetchDashboardStats = useCallback(async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(''); // Clear any previous errors
      const response = await api.get('/admin/dashboard/stats');
      // Extract stats from nested API response structure
      const statsData = response.data?.data?.stats || {};

      // Map the backend response to frontend interface
      const mappedStats: DashboardStats = {
        totalUsers: statsData.users?.total || 0,
        totalServers: statsData.servers?.total || 0,
        totalServices: statsData.services?.total || 0,
        totalInvoices: 0, // Not provided by backend yet
        activeServices: statsData.services?.active || 0,
        paidInvoices: 0, // Not provided by backend yet
        pendingInvoices: 0, // Not provided by backend yet
        monthlyRevenue: 0, // Not provided by backend yet
        recentUsers: [], // Not provided by backend yet
        recentServices: [], // Not provided by backend yet
        serverStatus: [], // Not provided by backend yet
      };

      setStats(mappedStats);
      setLastUpdate(new Date());

      // Update animated values immediately (disable animation to prevent resource issues)
      setAnimatedValues({
        totalUsers: mappedStats.totalUsers,
        totalServers: mappedStats.totalServers,
        totalServices: mappedStats.totalServices,
        activeServices: mappedStats.activeServices,
      });
    } catch (err: any) {
      setError('Erro ao carregar estatísticas do dashboard');
      console.error('Dashboard stats error:', err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []); // No dependencies needed

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 3;

    const loadStats = async () => {
      try {
        await fetchDashboardStats();
        consecutiveErrors = 0; // Reset on success
      } catch (error) {
        consecutiveErrors++;
        console.warn(`Dashboard stats error (${consecutiveErrors}/${maxConsecutiveErrors}):`, error);

        if (consecutiveErrors >= maxConsecutiveErrors) {
          console.error('Too many consecutive errors, stopping auto-refresh');
          setError('Erro de conexão. Atualização automática pausada.');
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
        }
      }
    };

    // Initial load
    loadStats();

    // Set up auto-refresh interval
    intervalId = setInterval(() => {
      // Skip if page is hidden or too many errors
      if (document.hidden || consecutiveErrors >= maxConsecutiveErrors) return;
      loadStats();
    }, 30000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []); // Empty dependency array - this effect runs only once


  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="text-sm text-red-700">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-white/20 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Dashboard Administrativo
              </h1>
              <p className="mt-2 text-gray-600 flex items-center">
                <Activity className="w-5 h-5 mr-2" />
                Visão geral do sistema em tempo real
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-sm text-gray-500 flex items-center">
                <Clock className="w-5 h-5 mr-1" />
                Última atualização: {lastUpdate.toLocaleTimeString('pt-BR')}
              </div>
              <button
                onClick={() => fetchDashboardStats(true)}
                disabled={isRefreshing}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-5 h-5 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Atualizando...' : 'Atualizar'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total de Usuários</p>
                <p className="text-3xl font-bold mt-1">
                  {animatedValues.totalUsers || stats?.totalUsers || 0}
                </p>
                <div className="flex items-center mt-2">
                  <ArrowUpRight className="w-4 h-4 text-green-300 mr-1" />
                  <span className="text-xs text-blue-100">+12% este mês</span>
                </div>
              </div>
              <div className="bg-white/20 rounded-full p-3">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm font-medium">Servidores</p>
                <p className="text-3xl font-bold mt-1">
                  {animatedValues.totalServers || stats?.totalServers || 0}
                </p>
                <div className="flex items-center mt-2">
                  <ArrowUpRight className="w-4 h-4 text-green-300 mr-1" />
                  <span className="text-xs text-emerald-100">+2 novos</span>
                </div>
              </div>
              <div className="bg-white/20 rounded-full p-3">
                <Server className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Serviços VoIP</p>
                <p className="text-3xl font-bold mt-1">
                  {animatedValues.totalServices || stats?.totalServices || 0}
                </p>
                <div className="flex items-center mt-2">
                  <ArrowUpRight className="w-4 h-4 text-green-300 mr-1" />
                  <span className="text-xs text-purple-100">+8% ativos</span>
                </div>
              </div>
              <div className="bg-white/20 rounded-full p-3">
                <Phone className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm font-medium">Receita Mensal</p>
                <p className="text-3xl font-bold mt-1">
                  {formatCurrency(stats?.monthlyRevenue || 0)}
                </p>
                <div className="flex items-center mt-2">
                  <ArrowUpRight className="w-4 h-4 text-green-300 mr-1" />
                  <span className="text-xs text-amber-100">+15% vs mês anterior</span>
                </div>
              </div>
              <div className="bg-white/20 rounded-full p-3">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

      {/* Secondary Stats - Mesmo tamanho e estilo dos principais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Serviços Ativos</p>
                <p className="text-3xl font-bold mt-1">
                  {animatedValues.activeServices || stats?.activeServices || 0}
                </p>
                <div className="flex items-center mt-2">
                  <CheckCircle className="w-4 h-4 text-green-300 mr-1" />
                  <span className="text-xs text-green-100">100% uptime</span>
                </div>
              </div>
              <div className="bg-white/20 rounded-full p-3">
                <Activity className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-cyan-100 text-sm font-medium">Faturas Pagas</p>
                <p className="text-3xl font-bold mt-1">
                  {stats?.paidInvoices || 0}
                </p>
                <div className="flex items-center mt-2">
                  <FileText className="w-4 h-4 text-cyan-300 mr-1" />
                  <span className="text-xs text-cyan-100">Este mês</span>
                </div>
              </div>
              <div className="bg-white/20 rounded-full p-3">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-rose-100 text-sm font-medium">Faturas Pendentes</p>
                <p className="text-3xl font-bold mt-1">
                  {stats?.pendingInvoices || 0}
                </p>
                <div className="flex items-center mt-2">
                  <Clock className="w-4 h-4 text-rose-300 mr-1" />
                  <span className="text-xs text-rose-100">Aguardando</span>
                </div>
              </div>
              <div className="bg-white/20 rounded-full p-3">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <Zap className="w-6 h-6 mr-2 text-blue-600" />
              Ações Rápidas
            </h3>
            <p className="text-sm text-gray-600 mt-1">Acesse as funções mais utilizadas</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={() => window.location.href = '/admin/users'}
                className="group flex items-center justify-center px-6 py-4 border-2 border-blue-200 rounded-xl text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105"
              >
                <Users className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform" />
                Gerenciar Usuários
              </button>
              <button
                onClick={() => window.location.href = '/admin/servers'}
                className="group flex items-center justify-center px-6 py-4 border-2 border-emerald-200 rounded-xl text-sm font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all duration-200 transform hover:scale-105"
              >
                <Server className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform" />
                Gerenciar Servidores
              </button>
              <button
                onClick={() => window.location.href = '/admin/push-notifications'}
                className="group flex items-center justify-center px-6 py-4 border-2 border-orange-200 rounded-xl text-sm font-semibold text-orange-700 bg-orange-50 hover:bg-orange-100 hover:border-orange-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all duration-200 transform hover:scale-105"
              >
                <Bell className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform" />
                Notificações Push
              </button>
              <button
                onClick={() => window.location.href = '/admin/security'}
                className="group flex items-center justify-center px-6 py-4 border-2 border-purple-200 rounded-xl text-sm font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 hover:border-purple-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200 transform hover:scale-105"
              >
                <Shield className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform" />
                Dashboard de Segurança
              </button>
            </div>
          </div>
        </div>

        {/* System Health Overview */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* System Status */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                <Activity className="w-6 h-6 mr-2 text-green-600" />
                Status do Sistema
              </h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-green-500 rounded-full mr-3 flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900">API Backend</span>
                  </div>
                  <span className="text-xs text-green-600 font-medium bg-green-100 px-2 py-1 rounded-full">Online</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-green-500 rounded-full mr-3 flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900">Banco de Dados</span>
                  </div>
                  <span className="text-xs text-green-600 font-medium bg-green-100 px-2 py-1 rounded-full">Online</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-blue-500 rounded-full mr-3 flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900">WebSocket</span>
                  </div>
                  <span className="text-xs text-blue-600 font-medium bg-blue-100 px-2 py-1 rounded-full">Ativo</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-amber-500 rounded-full mr-3 flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900">Cache Redis</span>
                  </div>
                  <span className="text-xs text-amber-600 font-medium bg-amber-100 px-2 py-1 rounded-full">Sincronizando</span>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                <Bell className="w-6 h-6 mr-2 text-blue-600" />
                Atividade Recente
              </h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <UserPlus className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Novo usuário registrado</p>
                    <p className="text-xs text-gray-500">João Silva - 2 minutos atrás</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <ServerCog className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Servidor sincronizado</p>
                    <p className="text-xs text-gray-500">Servidor BR-01 - 5 minutos atrás</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <Shield className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Alerta de segurança</p>
                    <p className="text-xs text-gray-500">Tentativa de login suspeita - 10 minutos atrás</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;